/**
 * A unique id that Interhome uses to identify partners.
 * Affects how partners pay commission.
 *
 * Format:
 * - starts with 2 letters followed by up to 8 numbers
 * - zeros after 2 letters can be omitted
 * - total length up to 10 characters
 *
 * @example ES50399, ES0050399, CH209999, IC1000099.
 *
 * @see {@link https://partners.interhome.group/wiki/:faq:general:names | Interhome codes, names, definitions}
 * @see {@link https://partners.interhome.group/wiki/faq:links:general | Linking to Interhome }
 */
export type PartnerId = string

const PARTNER_ID_REGEXP = /(?<partnerId>[A-Z]{2}[0-9]{1,8})([^0-9]|$)/

/**
 * @see https://jira.migros.net/browse/HHDWEBCC-4679
 */
export const PARTNER_ID_WHITELIST: ReadonlySet<string> = new Set([
  'IH1000019',
  'IH1000020',
  'IH1000021',
  'IH1000022',
  'IH1000023',
  'IH1000024',
  'IH1000025',
  'IH1000026',
  'IH1000027',
  'IH1000028',
  'IH1000029',
  'IH1000030',
  'IH1000031',
  'IH1000032',
  'IH1000033',
  'IH1000034',
  'IH1000035',
  'IH1000036',
  'IH1000223',
  'IH1000229',
  'CH1001778',
])

/**
 * Extracts a valid {@link PartnerId} from a string, if possible.
 *
 * Takes up to the first 10 characters.
 * If `partnerid` is longer than 10 characters, the `/webcc-api/v1/booking/checkPrice/final` request will fail.
 * On the details page this will result in the booking becoming impossible.
 *
 * @see https://jira.migros.net/browse/IHGWEBCC-643
 * @see https://jira.migros.net/browse/IHGWEBCC-1353
 */
export function getPartnerId(input: unknown): PartnerId | undefined {
  return input
    ?.toString()
    .match(PARTNER_ID_REGEXP)
    ?.groups?.partnerId.toUpperCase()
}

export function parsePartnerIdFromJWT(jwt: unknown): string | undefined {
  try {
    if (typeof jwt !== 'string') return
    return JSON.parse(jwt.split('.')[1]).Payload?.PartnerID
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    return
  }
}

export function isPartnerIdWhitelisted(partnerId: unknown): boolean {
  return typeof partnerId === 'string' && PARTNER_ID_WHITELIST.has(partnerId)
}

export function getPartnerIdForReferrer(
  referrer: string | undefined,
): string | undefined {
  if (!referrer) return

  return Object.entries({
    IH1000116: ['google.'],
    IH1000117: [
      'bing.',
      'ecosia.org',
      'duckduckgo.',
      'yahoo.',
      'baidu.',
      'seznam.',
      'qwant.com',
      'yandex.',
    ],
  }).find(([, substrings]) =>
    substrings.some((substring) => referrer.includes(substring)),
  )?.[0]
}
